/**
 * Top bar of the application shell.
 *
 * It owns the two controls that mirror the layout — the drawer toggle below `lg` and the rail toggle above it — plus
 * the three global preferences (language, theme, account). Nothing else belongs here: a top bar that also carries
 * page-level actions competes with the page's own header.
 */

import { ChevronsLeft, ChevronsRight, Menu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { LocaleSwitcher } from '@/components/layout/LocaleSwitcher';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { UserMenu } from '@/components/layout/UserMenu';
import { Button } from '@/components/ui/button';
import { useUiStore } from '@/stores/uiStore';

/** Sticky header of the authenticated shell. */
export function Topbar() {
  const { t } = useTranslation();
  const sidebarCollapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);
  const setMobileNavOpen = useUiStore((state) => state.setMobileNavOpen);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
      <div className="flex h-14 items-center gap-1 px-3 lg:px-6">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-label={t('nav.openMenu')}
          onClick={() => setMobileNavOpen(true)}
        >
          <Menu aria-hidden className="h-5 w-5" />
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="hidden lg:inline-flex"
          aria-label={sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          aria-expanded={!sidebarCollapsed}
          onClick={toggleSidebar}
        >
          {sidebarCollapsed ? (
            <ChevronsRight aria-hidden className="h-4 w-4 rtl:rotate-180" />
          ) : (
            <ChevronsLeft aria-hidden className="h-4 w-4 rtl:rotate-180" />
          )}
        </Button>

        <div className="flex-1" />

        <LocaleSwitcher className="hidden sm:block" />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
